import React, { useState } from 'react';
import {
  Autocomplete, Box, Button, Checkbox, Chip, FormControlLabel, IconButton, Paper, Stack, TextField, Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  featuredOf, plural, productsWithTag, pruneExcluded, scopeFromTours, tagLabel, useTagScope,
} from './tagScope';

const uniqById = (list) => list.filter((p, i) => p && list.findIndex((q) => q?.id === p.id) === i);
const has = (list, x) => list.some((y) => y.id === x.id);
const countLabel = (n) => `${n} product${n === 1 ? '' : 's'}`;
// A product list longer than this gets a filter box.
const PANEL_FILTER_OVER = 8;

/*
 * A "+ Other …" search box: a checkbox list that stays open while you tick (picking products one
 * at a time, the box closing after each, took Nhue longer than writing the answer). Each tick
 * applies at once; the box closes on blur or Escape. `keyOf(option)` is unique across `options`.
 */
function SearchBox({ label, options, keyOf, isOn, onToggle, onClose, groupBy }) {
  const [input, setInput] = useState('');
  return (
    <Autocomplete
      multiple
      size="small"
      openOnFocus
      disableCloseOnSelect
      disableClearable
      options={options}
      value={options.filter(isOn)}
      inputValue={input}
      // MUI empties the search after each pick; keep it, to tick several matches in a row.
      onInputChange={(e, v, reason) => { if (reason !== 'reset') setInput(v); }}
      isOptionEqualToValue={(a, b) => keyOf(a) === keyOf(b)}
      getOptionLabel={(o) => o.name}
      groupBy={groupBy}
      renderTags={() => null}
      onChange={(e, v, reason, details) => { if (details?.option) onToggle(details.option); }}
      onClose={(e, reason) => { if (reason === 'escape' || reason === 'blur') onClose(); }}
      onBlur={onClose}
      onKeyDown={(e) => {
        // Backspace in an empty box would untick the last pick, out of sight.
        if (e.key === 'Backspace' && !input) e.defaultMuiPrevented = true;
        if (e.key === 'Escape') onClose();
      }}
      renderOption={(props, o, { selected }) => (
        <li {...props} key={keyOf(o)}>
          <Checkbox size="small" checked={selected} tabIndex={-1} disableRipple sx={{ p: 0.25, mr: 1 }} />
          {o.name}
          {o.product_count != null && (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>{countLabel(o.product_count)}</Typography>
          )}
        </li>
      )}
      sx={{ minWidth: 280 }}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus
          placeholder={label}
          inputProps={{ ...params.inputProps, 'aria-label': label }}
        />
      )}
    />
  );
}

// The products a picked tag (or "All products") covers, all ticked but the ones left out.
function ProductsPanel({ name, products, isExcluded, onToggle, onDone, isTag, disabled }) {
  const [filter, setFilter] = useState('');
  const q = filter.trim().toLowerCase();
  const listed = q ? products.filter((p) => p.name.toLowerCase().includes(q)) : products;
  const included = products.filter((p) => !isExcluded(p)).length;
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: '10px', maxWidth: 560 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{name}</Typography>
        <Button size="small" onClick={onDone}>Done</Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" component="div">
        {included} of {countLabel(products.length)} included.
        {!disabled && ' Untick a product to leave it out.'}
        {isTag && ' Products added to this tag later are included.'}
      </Typography>
      {products.length > PANEL_FILTER_OVER && (
        <TextField
          size="small"
          placeholder="Filter products"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          inputProps={{ 'aria-label': `Filter products in ${name}` }}
          sx={{ mt: 1, width: '100%', maxWidth: 320 }}
        />
      )}
      <Box sx={{ mt: 0.5, maxHeight: 280, overflowY: 'auto' }}>
        {listed.map((p) => (
          <FormControlLabel
            key={p.id}
            sx={{ display: 'flex', mr: 0 }}
            control={<Checkbox size="small" checked={!isExcluded(p)} onChange={() => onToggle(p)} disabled={disabled} />}
            label={<Typography variant="body2">{p.name}</Typography>}
          />
        ))}
        {!listed.length && <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No products match.</Typography>}
      </Box>
    </Paper>
  );
}

/*
 * "Applies to: [✓ All products] [+ Colosseum collection] [+ Vatican Museums] [+ Other collection]
 * [+ Other destination] [+ Other tag] [+ Other product]" -- which products an answer is for,
 * picked directly (same pill style as ChannelChips). Scope semantics: tagScope.js.
 *
 *  - "All products" is on until something is picked; picking anything switches it off. Turning
 *    the last pick off switches it back on, so an answer never applies to nothing and there's no
 *    "pick at least one" error to show.
 *  - The pills offered are `suggested` (the products guests asked about) plus whatever is
 *    already picked -- not the whole catalog, which runs to hundreds of products at some
 *    operators. With featured categories the suggested products are offered as their tags in the
 *    FIRST one ("+ Colosseum collection"); a product without one is still offered as itself.
 *  - Each featured category has its own "+ Other <category>" search; every other System tag and
 *    the Access tags share "+ Other tag"; "+ Other product" searches `options` for the rest.
 *  - A picked tag, and "All products" while on, opens (the arrow next to it) to its products,
 *    all ticked; unticking one leaves it out ("Granada destination · 2 excluded").
 *
 * Controlled: allTours / tours / collections / accessTags / excludedTours in,
 * onChange({ all_tours, tours, collections, access_tags, excluded_tours }) out.
 */
export default function ProductChips({
  allTours, tours, collections = [], accessTags = [], excludedTours = [], suggested = [], options = [], onChange, disabled,
}) {
  const ctx = useTagScope();
  // null | 'tag' | 'product' | a featured category's code: which search box is open.
  const [searching, setSearching] = useState(null);
  // Whose products are listed: 'all', 'c<tag id>' or 'a<access tag id>'.
  const [expanded, setExpanded] = useState(null);
  // Picked when the editor opened, or through search: stay on the row even if switched off again
  // (unticked in a tag's list, a product picked on its own goes off but can be picked again).
  const [added, setAdded] = useState(() => (allTours ? [] : tours));
  const [addedTags, setAddedTags] = useState(() => (allTours ? [] : collections));
  const [addedAtags, setAddedAtags] = useState(() => (allTours ? [] : accessTags));
  const selected = allTours ? [] : tours;
  const selectedTags = allTours ? [] : collections;
  const selectedAtags = allTours ? [] : accessTags;
  const excluded = excludedTours;
  const suggestion = scopeFromTours(suggested, ctx);
  const shown = uniqById([...suggestion.tours, ...added, ...selected]);
  const shownTags = uniqById([...suggestion.collections, ...addedTags, ...selectedTags]);
  const shownAtags = uniqById([...addedAtags, ...selectedAtags]);
  const isOn = (p) => has(selected, p);
  const isTagOn = (t) => has(selectedTags, t);
  const isAtagOn = (a) => has(selectedAtags, a);
  const isExcluded = (p) => has(excluded, p);

  const ALL = { all_tours: true, tours: [], collections: [], access_tags: [] };
  // Nothing picked = all products; exclusions no picked tag covers any more are dropped.
  const emit = ({ tours: t = selected, tags = selectedTags, atags = selectedAtags, excluded: x = excluded }) => onChange(
    t.length || tags.length || atags.length
      ? { all_tours: false, tours: t, collections: tags, access_tags: atags, excluded_tours: pruneExcluded(x, ctx, tags, atags) }
      : { ...ALL, excluded_tours: [] },
  );
  const toggle = (p) => emit(isOn(p)
    ? { tours: selected.filter((t) => t.id !== p.id) }
    // Picked on its own, a product is in even where a tag's list left it out.
    : { tours: [...selected, p], excluded: excluded.filter((x) => x.id !== p.id) });
  const toggleTag = (t) => emit({ tags: isTagOn(t) ? selectedTags.filter((s) => s.id !== t.id) : [...selectedTags, t] });
  const toggleAtag = (a) => emit({ atags: isAtagOn(a) ? selectedAtags.filter((s) => s.id !== a.id) : [...selectedAtags, a] });
  // Unticked in a list: left out; ticked again: back in. Leaving out a product that was also
  // picked on its own unpicks it.
  const toggleExcluded = (p) => {
    const leaving = !isExcluded(p);
    const next = leaving ? [...excluded, { id: p.id, name: p.name }] : excluded.filter((x) => x.id !== p.id);
    if (allTours) onChange({ ...ALL, excluded_tours: next });
    else emit({ tours: leaving ? selected.filter((t) => t.id !== p.id) : selected, excluded: next });
  };

  // From a search box: also kept on the row.
  const pickProduct = (p) => { setAdded((a) => uniqById([...a, p])); toggle(p); };
  const pickTag = ({ kind, ...t }) => {
    if (kind === 'atag') {
      setAddedAtags((a) => uniqById([...a, t]));
      toggleAtag(t);
    } else {
      setAddedTags((a) => uniqById([...a, t]));
      toggleTag(t);
    }
  };

  // What a pill covers, for its list: every product for "All products".
  const coveredBy = (key) => (key === 'all' ? options : productsWithTag(options, ctx, key[0] === 'a' ? 'atag' : 'tag', Number(key.slice(1))));
  const isPicked = (key) => {
    if (key === 'all') return allTours;
    const id = Number(key.slice(1));
    return (key[0] === 'a' ? selectedAtags : selectedTags).some((t) => t.id === id);
  };
  const openKey = expanded && isPicked(expanded) ? expanded : null;
  const openKind = openKey?.[0] === 'a' ? 'atag' : 'tag';
  const openTag = openKey && openKey !== 'all'
    ? (openKind === 'atag' ? selectedAtags : selectedTags).find((t) => t.id === Number(openKey.slice(1)))
    : null;

  const pill = ({ key, label, on, onClick, ariaLabel, title }) => (
    <Chip
      key={key}
      size="small"
      icon={on ? <CheckIcon /> : <AddIcon />}
      label={label}
      title={title}
      color={on ? 'primary' : 'default'}
      variant={on ? 'filled' : 'outlined'}
      onClick={disabled ? undefined : onClick}
      aria-pressed={disabled ? undefined : on}
      aria-label={ariaLabel}
      sx={on ? undefined : { color: 'text.secondary', borderStyle: 'dashed' }}
    />
  );
  // A picked pill with products to list gets an arrow that opens the list.
  const withList = (key, chip, listName) => {
    if (!coveredBy(key).length) return chip;
    const open = openKey === key;
    return (
      <Box key={key} sx={{ display: 'inline-flex', alignItems: 'center' }}>
        {chip}
        <IconButton
          size="small"
          aria-label={key === 'all' ? 'Show all products' : `Show products in ${listName}`}
          aria-expanded={open}
          onClick={() => setExpanded(open ? null : key)}
          sx={{ p: 0.25 }}
        >
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
    );
  };

  const tagPill = (t, kind) => {
    const key = `${kind === 'atag' ? 'a' : 'c'}${t.id}`;
    const label = tagLabel(t, ctx, kind);
    const on = kind === 'atag' ? isAtagOn(t) : isTagOn(t);
    const out = on ? coveredBy(key).filter(isExcluded).length : 0;
    const chip = pill({
      key,
      label: out ? `${label} · ${out} excluded` : label,
      title: t.product_count ? countLabel(t.product_count) : undefined,
      on,
      onClick: () => (kind === 'atag' ? toggleAtag(t) : toggleTag(t)),
      ariaLabel: `${label}: ${on ? 'selected' : 'not selected'}${out ? `, ${out} excluded` : ''}`,
    });
    return on ? withList(key, chip, label) : chip;
  };

  const allOut = allTours ? excluded.length : 0;
  const allPill = pill({
    key: 'all',
    label: allOut ? `All products except ${allOut}` : 'All products',
    on: allTours,
    // Already on: nothing to do (it switches off by picking something).
    onClick: allTours ? undefined : () => onChange({ ...ALL, excluded_tours: [] }),
    ariaLabel: `All products: ${allTours ? 'selected' : 'not selected'}${allOut ? `, ${allOut} excluded` : ''}`,
  });

  const otherTags = shownTags.filter((t) => !featuredOf(ctx, t));
  const tagOptions = [
    ...ctx.otherTags.map((t) => ({ ...t, kind: 'tag' })),
    ...ctx.accessTags.map((a) => ({ ...a, kind: 'atag' })),
  ];
  const tagKey = (o) => `${o.kind}${o.id}`;

  let searchBox = null;
  if (searching === 'product') {
    searchBox = (
      <SearchBox
        label="Search products"
        options={options}
        keyOf={(o) => o.id}
        isOn={isOn}
        onToggle={pickProduct}
        onClose={() => setSearching(null)}
      />
    );
  } else if (searching === 'tag') {
    searchBox = (
      <SearchBox
        label="Search tags"
        options={tagOptions}
        keyOf={tagKey}
        isOn={(o) => (o.kind === 'atag' ? isAtagOn(o) : isTagOn(o))}
        onToggle={pickTag}
        onClose={() => setSearching(null)}
        groupBy={(o) => (o.kind === 'atag' ? 'Access tags' : o.category.name)}
      />
    );
  } else if (searching) {
    const f = ctx.featured.find((x) => x.code === searching);
    searchBox = f && (
      <SearchBox
        label={`Search ${plural(f.name.toLowerCase())}`}
        options={f.tags}
        keyOf={(o) => o.id}
        isOn={isTagOn}
        onToggle={(t) => pickTag({ ...t, kind: 'tag' })}
        onClose={() => setSearching(null)}
      />
    );
  }

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary">Applies to:</Typography>
        {allTours ? withList('all', allPill) : allPill}
        {ctx.featured.map((f) => shownTags.filter((t) => featuredOf(ctx, t)?.code === f.code).map((t) => tagPill(t, 'tag')))}
        {otherTags.map((t) => tagPill(t, 'tag'))}
        {shownAtags.map((a) => tagPill(a, 'atag'))}
        {shown.map((p) => pill({
          key: p.id,
          label: p.name,
          on: isOn(p),
          onClick: () => toggle(p),
          ariaLabel: `${p.name}: ${isOn(p) ? 'selected' : 'not selected'}`,
        }))}
        {!disabled && (searchBox || (
          <>
            {ctx.featured.map((f) => f.tags.some((t) => !has(shownTags, t)) && pill({
              key: `other-${f.code}`,
              label: `Other ${f.name.toLowerCase()}`,
              on: false,
              onClick: () => setSearching(f.code),
              ariaLabel: `Add another ${f.name.toLowerCase()}`,
            }))}
            {tagOptions.some((o) => !has(o.kind === 'atag' ? shownAtags : otherTags, o)) && pill({
              key: 'other-tag',
              label: 'Other tag',
              on: false,
              onClick: () => setSearching('tag'),
              ariaLabel: 'Add another tag',
            })}
            {options.some((o) => !has(shown, o)) && pill({
              key: 'other',
              label: 'Other product',
              on: false,
              onClick: () => setSearching('product'),
              ariaLabel: 'Add another product',
            })}
          </>
        ))}
      </Stack>
      {openKey && (
        <ProductsPanel
          key={openKey}
          name={openTag ? tagLabel(openTag, ctx, openKind) : 'All products'}
          isTag={!!openTag}
          products={coveredBy(openKey)}
          isExcluded={isExcluded}
          onToggle={toggleExcluded}
          onDone={() => setExpanded(null)}
          disabled={disabled}
        />
      )}
    </Stack>
  );
}
